import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Sparkles, 
  Search,
  Loader2,
  Play,
  Clock,
  TrendingUp,
  Zap,
  Heart,
  Share2,
  MessageCircle,
  Copy,
  Check,
  History,
  Trash2,
  ExternalLink,
  Film,
  Wand2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const ViralScoreBadge = ({ score }) => {
  const getColor = () => {
    if (score >= 80) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (score >= 60) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (score >= 40) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  return (
    <Badge variant="outline" className={`${getColor()} font-bold`}>
      <Zap className="w-3 h-3 mr-1" />
      {score}%
    </Badge>
  );
};

const EmotionBadge = ({ emotion }) => {
  const config = {
    excited: { icon: '🔥', color: 'bg-orange-500/20 text-orange-400' },
    funny: { icon: '😂', color: 'bg-yellow-500/20 text-yellow-400' },
    controversial: { icon: '💥', color: 'bg-red-500/20 text-red-400' },
    educational: { icon: '📚', color: 'bg-blue-500/20 text-blue-400' },
    inspirational: { icon: '✨', color: 'bg-purple-500/20 text-purple-400' },
    neutral: { icon: '💬', color: 'bg-gray-500/20 text-gray-400' }
  };

  const cfg = config[emotion] || config.neutral;

  return (
    <Badge variant="outline" className={cfg.color}>
      {cfg.icon} {emotion}
    </Badge>
  );
};

const MomentCard = ({ moment, onCreateClip }) => {
  const [copied, setCopied] = useState(false);

  const copyCaption = () => {
    navigator.clipboard.writeText(moment.suggested_caption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Caption copiada!');
  };

  return (
    <Card className="glass-card overflow-hidden" data-testid={`viral-moment-${moment.id}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-2">
            <ViralScoreBadge score={moment.viral_score} />
            <EmotionBadge emotion={moment.emotion} />
          </div>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDuration(moment.start_time)} - {formatDuration(moment.end_time)}
          </span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <p className="text-sm line-clamp-3">{moment.text}</p>
        
        {/* Metrics */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-muted/30 rounded-lg p-2">
            <p className="text-xs text-muted-foreground">Hook</p>
            <p className="font-bold text-sm">{moment.hook_quality}%</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-2">
            <p className="text-xs text-muted-foreground">Share</p>
            <p className="font-bold text-sm">{moment.shareability}%</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-2">
            <p className="text-xs text-muted-foreground">Duração</p>
            <p className="font-bold text-sm">{formatDuration(moment.duration)}</p>
          </div>
        </div>

        {/* Engagement Factors */}
        <div className="flex flex-wrap gap-1">
          {moment.engagement_factors.slice(0, 3).map((factor, i) => (
            <Badge key={`${moment.id}-factor-${i}`} variant="secondary" className="text-xs">
              {factor}
            </Badge>
          ))}
        </div>

        {/* Suggested Caption */}
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex justify-between items-start mb-1">
            <p className="text-xs text-muted-foreground">Caption sugerida</p>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2"
              onClick={copyCaption}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </Button>
          </div>
          <p className="text-sm">{moment.suggested_caption}</p>
        </div>

        {/* Hashtags */}
        <div className="flex flex-wrap gap-1">
          {moment.suggested_hashtags.map((tag, i) => (
            <span key={`${moment.id}-tag-${i}`} className="text-xs text-primary">{tag}</span>
          ))}
        </div>

        {/* Actions */}
        <Button 
          className="w-full" 
          onClick={() => onCreateClip(moment)}
          data-testid={`create-clip-${moment.id}`}
        >
          <Wand2 className="w-4 h-4 mr-2" />
          Criar Clip
        </Button>
      </CardContent>
    </Card>
  );
};

export default function ViralDetection() {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [createClipDialog, setCreateClipDialog] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await api.get('/viral/history');
      setHistory(response.data.analyses || []);
    } catch (error) {
      logger.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Poll for job status
  useEffect(() => {
    if (!currentJob || currentJob.status === 'completed' || currentJob.status === 'failed') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/viral/detect/${currentJob.job_id}`);
        setCurrentJob(response.data);

        if (response.data.status === 'completed') {
          setResult(response.data.result);
          setAnalyzing(false);
          toast.success('Análise concluída! Momentos virais detectados.');
          fetchHistory();
        } else if (response.data.status === 'failed') {
          setAnalyzing(false);
          toast.error('Falha na análise: ' + (response.data.error || 'Erro desconhecido'));
        }
      } catch (error) {
        logger.error('Failed to check job status:', error);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentJob, fetchHistory]);

  const handleAnalyze = async () => {
    if (!youtubeUrl.trim()) {
      toast.error('Cole uma URL do YouTube');
      return;
    }

    if (!youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      toast.error('URL inválida. Use uma URL do YouTube.');
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const response = await api.post('/viral/detect', {
        youtube_url: youtubeUrl,
        language: 'pt'
      });

      setCurrentJob(response.data);
      toast.info('Análise iniciada. Isso pode levar alguns minutos...');
    } catch (error) {
      setAnalyzing(false);
      toast.error('Erro ao iniciar análise: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateClip = (moment) => {
    setSelectedMoment(moment);
    setCreateClipDialog(true);
  };

  const handleDeleteHistory = async (jobId) => {
    try {
      await api.delete(`/viral/history/${jobId}`);
      setHistory(prev => prev.filter(h => h.job_id !== jobId));
      toast.success('Análise removida');
    } catch (error) {
      toast.error('Erro ao remover análise');
    }
  };

  const loadFromHistory = (analysis) => {
    setResult(analysis.result);
    setYoutubeUrl(analysis.youtube_url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-primary" />
          Detecção de Momentos Virais
        </h1>
        <p className="text-muted-foreground">
          IA analisa vídeos do YouTube e identifica os melhores momentos para clips
        </p>
      </div>

      {/* URL Input */}
      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cole a URL do vídeo do YouTube..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="pl-10"
                data-testid="youtube-url-input"
              />
            </div>
            <Button 
              onClick={handleAnalyze} 
              disabled={analyzing}
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analisar
                </>
              )}
            </Button>
          </div>

          {/* Progress */}
          {currentJob && currentJob.status !== 'completed' && currentJob.status !== 'failed' && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {currentJob.status === 'downloading' && 'Baixando vídeo...'}
                  {currentJob.status === 'transcribing' && 'Transcrevendo com Whisper...'}
                  {currentJob.status === 'analyzing' && 'Analisando momentos virais com IA...'}
                  {currentJob.status === 'pending' && 'Iniciando análise...'}
                </span>
                <span>{currentJob.progress}%</span>
              </div>
              <Progress value={currentJob.progress} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Results and History */}
      <Tabs defaultValue="results" className="space-y-4">
        <TabsList>
          <TabsTrigger value="results" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Resultados
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="results">
          {result ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="glass-card">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Momentos Detectados</p>
                    <p className="text-2xl font-bold">{result.viral_moments?.length || 0}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Duração Total</p>
                    <p className="text-2xl font-bold">{formatDuration(result.total_duration || 0)}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Melhor Score</p>
                    <p className="text-2xl font-bold text-green-400">
                      {result.best_moments?.[0]?.viral_score || 0}%
                    </p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">Idioma</p>
                    <p className="text-2xl font-bold uppercase">{result.language || 'PT'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Best Moments */}
              {result.best_moments && result.best_moments.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Top 5 Momentos Virais
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {result.best_moments.map((moment, index) => (
                      <MomentCard 
                        key={moment.id || index} 
                        moment={moment} 
                        onCreateClip={handleCreateClip}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All Moments */}
              {result.viral_moments && result.viral_moments.length > 5 && (
                <div>
                  <h2 className="text-lg font-semibold mb-4">Outros Momentos</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {result.viral_moments.slice(5).map((moment, index) => (
                      <MomentCard 
                        key={moment.id || index + 5} 
                        moment={moment} 
                        onCreateClip={handleCreateClip}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhuma análise ainda</h3>
                <p className="text-muted-foreground">
                  Cole uma URL do YouTube acima para detectar momentos virais
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          {loadingHistory ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Sem histórico</h3>
                <p className="text-muted-foreground">
                  Suas análises anteriores aparecerão aqui
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {history.map((analysis) => (
                <Card 
                  key={analysis.job_id} 
                  className="glass-card hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => loadFromHistory(analysis)}
                >
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{analysis.youtube_url}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(analysis.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {analysis.result?.viral_moments?.length || 0} momentos
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteHistory(analysis.job_id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    {analysis.result?.best_moments?.[0] && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Melhor:</span>
                        <ViralScoreBadge score={analysis.result.best_moments[0].viral_score} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Clip Dialog */}
      <Dialog open={createClipDialog} onOpenChange={setCreateClipDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Clip</DialogTitle>
          </DialogHeader>
          {selectedMoment && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm mb-2">{selectedMoment.text}</p>
                <div className="flex items-center gap-2">
                  <ViralScoreBadge score={selectedMoment.viral_score} />
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(selectedMoment.start_time)} - {formatDuration(selectedMoment.end_time)}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                O clip será criado com base neste momento e publicado nas plataformas conectadas.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateClipDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={() => {
              toast.success('Clip adicionado à fila de criação!');
              setCreateClipDialog(false);
            }}>
              <Wand2 className="w-4 h-4 mr-2" />
              Criar Clip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
